import React from 'react';
import { connect } from 'react-redux';
import {
	TagOutlined,
	PlusOutlined,
	EditOutlined,
	SearchOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import {
	Button,
	Drawer,
	Input,
	List,
	Tooltip,
	Modal,
	message,
	Spin,
	ConfigProvider,
	Empty,
} from 'antd';
import { isEqual } from 'lodash';
import { Tag, TagEntityType, CreateTagRequest, UpdateTagRequest } from '../../types/tags';
import { tagsOnPartsRestCrudThunksForSupplier } from '../../thunks/tags_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { RootState } from '../../reducers/root_reducer';
import { withUserType } from '../withUserType';
import { ColorPicker } from './ColorPicker';
import { generateRandomColor, generateTempTagId } from '../../constants/tagConstants';
import PaginatedReduxList from '../paginated_redux_list/PaginatedReduxList';
import TagDisplay from '../TagDisplay';
import { EmptyState } from '../empty_state/EmptyState';

type TagCreationOwnProps = {
	targetCollectionName: TagEntityType;
};

type TagCreationProps = TagCreationOwnProps & {
	userType?: string;
	tags: Tag[];
	loading: boolean;
	createTag: (tag: CreateTagRequest) => Promise<any>;
	updateTag: (tag: Tag & UpdateTagRequest) => Promise<any>;
	deleteTag: (tagId: number) => Promise<any>;
};

function TagCreation({
	tags,
	loading,
	createTag,
	updateTag,
	deleteTag,
	targetCollectionName,
}: TagCreationProps): React.ReactElement {
	const [drawerOpen, setDrawerOpen] = React.useState(false);
	const [isEditing, setIsEditing] = React.useState(false);
	const [searchText, setSearchText] = React.useState('');
	const [draftTags, setDraftTags] = React.useState<Tag[]>([]);
	const [editingTagId, setEditingTagId] = React.useState<number | null>(null);
	const [showSearchInput, setShowSearchInput] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [searchFilters, setSearchFilters] = React.useState({});

	const hasUnsavedChanges = React.useMemo(() => {
		if (!isEditing) return false;
		return !isEqual(tags, draftTags);
	}, [tags, draftTags, isEditing]);

	const filteredTags = isEditing ? draftTags : tags;

	React.useEffect(() => {
		if (!isEditing && drawerOpen) {
			const timer = setTimeout(() => {
				const trimmedSearch = searchText.trim();
				setSearchFilters(trimmedSearch ? { search: trimmedSearch } : {});
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [searchText, isEditing, drawerOpen]);

	const openDrawer = () => {
		setDrawerOpen(true);
	};

	const resetAllState = () => {
		setIsEditing(false);
		setDraftTags([]);
		setEditingTagId(null);
		setSearchText('');
		setShowSearchInput(false);
		setSearchFilters({});
	};

	const showDiscardConfirmation = (onDiscard: () => void) => {
		Modal.confirm({
			title: 'Discard changes?',
			content: 'Your unsaved changes will be lost.',
			okText: 'Keep Editing',
			cancelText: 'Discard Changes',
			onCancel: onDiscard,
		});
	};

	const requestClose = () => {
		if (isEditing && hasUnsavedChanges) {
			showDiscardConfirmation(() => {
				setDrawerOpen(false);
				resetAllState();
			});
			return;
		}
		setDrawerOpen(false);
		resetAllState();
	};

	const startEditing = () => {
		setDraftTags([...tags]);
		setIsEditing(true);
	};

	const exitEditMode = () => {
		setIsEditing(false);
		setDraftTags([]);
		setEditingTagId(null);
	};

	const cancelEditing = () => {
		if (hasUnsavedChanges) {
			showDiscardConfirmation(exitEditMode);
			return;
		}
		exitEditMode();
	};

	const saveEdits = async () => {
		for (const tag of draftTags) {
			if (!tag.label.trim()) {
				message.warning('Please fill in all tag labels or remove empty tags');
				return;
			}
		}

		setIsSaving(true);
		try {
			const newTags = draftTags.filter((tag) => tag.id < 0);

			const deletedTags = tags.filter((tag) => !draftTags.find((dt) => dt.id === tag.id));

			const modifiedTags = draftTags.filter((draftTag) => {
				if (draftTag.id < 0) return false;
				const originalTag = tags.find((t) => t.id === draftTag.id);
				if (!originalTag) return false;
				return originalTag.label !== draftTag.label || originalTag.color !== draftTag.color;
			});

			const operations: Promise<any>[] = [];

			newTags.forEach((tag) => {
				const createRequest: CreateTagRequest = {
					label: tag.label,
					color: tag.color,
				};
				operations.push(createTag(createRequest));
			});

			modifiedTags.forEach((tag) => {
				operations.push(
					updateTag({
						id: tag.id,
						label: tag.label,
						color: tag.color,
					})
				);
			});

			deletedTags.forEach((tag) => {
				operations.push(deleteTag(tag.id));
			});

			await Promise.all(operations);

			// Clear search - PaginatedReduxList will reload automatically when filters change
			setSearchText('');
			setShowSearchInput(false);
			setSearchFilters({}); // Clear filters to show all tags
			exitEditMode();
			message.success('Tags saved successfully');
		} catch (error) {
			console.error('Error saving tags:', error);
			message.error(error || 'Failed to save tags');
		} finally {
			setIsSaving(false);
		}
	};

	const addNewTag = () => {
		const tempTag: Tag = {
			id: generateTempTagId(),
			label: '',
			color: generateRandomColor(),
		};
		setDraftTags((prev) => [...prev, tempTag]);
	};

	const handleDeleteTag = (tagId: number) => {
		setDraftTags((prev) => prev.filter((t) => t.id !== tagId));
	};

	const handleUpdateTagColor = (tagId: number, color: string) => {
		setDraftTags((prev) => prev.map((t) => (t.id === tagId ? { ...t, color } : t)));
		setEditingTagId(null);
	};

	const renderTagItem = (item: Tag) => (
		<div key={item.id} className="pb-2">
			<TagDisplay className="w-full" tag={item} showassociatedPartsCount />
		</div>
	);

	return (
		<div>
			<Button size="large" icon={<TagOutlined />} onClick={openDrawer}>
				Tags
			</Button>
			<Drawer
				title={
					<div className="flex w-full items-center justify-start">
						<TagOutlined />
						<span className="h5Medium ml-2">{isEditing ? 'Edit Tags' : 'Tags'}</span>
					</div>
				}
				visible={drawerOpen}
				width={420}
				onClose={requestClose}
				extra={
					!isEditing ? (
						<div className="flex w-full items-center justify-end gap-x-3">
							{showSearchInput ? (
								<Input
									allowClear
									size="small"
									placeholder="Search tags..."
									prefix={<SearchOutlined />}
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									onBlur={() => {
										if (!searchText.trim()) {
											setShowSearchInput(false);
										}
									}}
									autoFocus
									className="w-50"
								/>
							) : (
								<Tooltip title="Search Tags">
									<Button
										type="text"
										icon={<SearchOutlined />}
										onClick={() => setShowSearchInput(true)}
									/>
								</Tooltip>
							)}
							<Tooltip title="Edit Tags">
								<Button type="text" icon={<EditOutlined />} onClick={startEditing} />
							</Tooltip>
						</div>
					) : null
				}
			>
				{!isEditing ? (
					// @ts-ignore - Type inference issue with Redux connected component
					<ConfigProvider
						renderEmpty={() => (
							<EmptyState body="No tags yet. Click edit to create tags" height="auto" />
						)}
					>
						<PaginatedReduxList
							collectionName="tags"
							targetCollectionName={targetCollectionName}
							listItemRenderFunction={renderTagItem}
							fetchData={tagsOnPartsRestCrudThunksForSupplier.read}
							initialPagination={{ current: 1, pageSize: 10 }}
							initialSorting={{ sort_by: 'label', order: 'asc' }}
							initialFilters={{}}
							additionalParams={searchFilters}
							itemLayout="horizontal"
						/>
					</ConfigProvider>
				) : (
					<Spin spinning={loading}>
						<div>
							{filteredTags.length === 0 ? (
								<div className="bodyregular pb-5 text-center text-gray-500">No tags yet</div>
							) : (
								<List
									rowKey="id"
									dataSource={filteredTags}
									renderItem={(item) => {
										const isNewTag = item.id < 0;
										return (
											<List.Item className="px-0 py-2">
												<div className="flex w-full items-center gap-x-3">
													<ColorPicker
														currentColor={item.color}
														onColorChange={(color) => handleUpdateTagColor(item.id, color)}
														onCancel={() => setEditingTagId(null)}
														visible={editingTagId === item.id}
														onVisibleChange={(visible) => setEditingTagId(visible ? item.id : null)}
													/>

													<Input
														value={item.label}
														onChange={(e) =>
															setDraftTags((prev) =>
																prev.map((tag) =>
																	tag.id === item.id ? { ...tag, label: e.target.value } : tag
																)
															)
														}
														className="mr-2 flex-1"
														placeholder="Tag label"
														size="small"
														autoFocus={isNewTag}
													/>

													<Button
														icon={<DeleteOutlined />}
														onClick={() => handleDeleteTag(item.id)}
													/>
												</div>
											</List.Item>
										);
									}}
								/>
							)}

							<Button icon={<PlusOutlined />} onClick={addNewTag} type="link">
								Add Tag
							</Button>

							<div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
								<Button onClick={cancelEditing} disabled={isSaving}>
									Cancel
								</Button>
								<Button
									type="primary"
									onClick={saveEdits}
									disabled={!hasUnsavedChanges || isSaving}
									loading={isSaving}
								>
									Save
								</Button>
							</div>
						</div>
					</Spin>
				)}
			</Drawer>
		</div>
	);
}

const mapStateToProps = (
	state: RootState,
	ownProps: TagCreationOwnProps & { userType: string }
) => ({
	tags: getRecordsForTargetCollection(state.tags, ownProps.targetCollectionName) || [],
	loading:
		state.tags &&
		state.tags[ownProps.targetCollectionName] &&
		state.tags[ownProps.targetCollectionName].fetching,
});

const mapDispatchToProps = (
	dispatch: any,
	ownProps: TagCreationOwnProps & { userType: string }
) => ({
	createTag: (tag: CreateTagRequest) =>
		dispatch(tagsOnPartsRestCrudThunksForSupplier.create(tag, ownProps.targetCollectionName)),
	updateTag: (tag: Tag & UpdateTagRequest) =>
		dispatch(tagsOnPartsRestCrudThunksForSupplier.update(tag)),
	deleteTag: (tagId: number) =>
		dispatch(tagsOnPartsRestCrudThunksForSupplier.delete({ id: tagId }, 'id')),
});

// @ts-ignore - Type inference issue with HOC composition, same pattern works in TagSelector
export default withUserType(connect(mapStateToProps, mapDispatchToProps)(TagCreation));
